import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function decodePolyline(encoded) {
  if (!encoded) return [];
  let index = 0;
  const len = encoded.length;
  let lat = 0;
  let lng = 0;
  const coordinates = [];
  while (index < len) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dlat;
    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += dlng;
    coordinates.push([lat / 1e5, lng / 1e5]);
  }
  return coordinates;
}

function destroyMap(map) {
  if (!map) return;
  try {
    map.stop();
    map.off();
    map.remove();
  } catch {
    /* già rimosso */
  }
}

function LeafletMapCanvas({ mapKey, center, points, fermate, sede }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef(null);

  // Inizializza la mappa una sola volta per mapKey
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    destroyMap(mapRef.current);
    mapRef.current = null;
    layersRef.current = null;

    if (el._leaflet_id != null) {
      el.replaceChildren();
      delete el._leaflet_id;
    }

    const map = L.map(el, {
      scrollWheelZoom: false,
      fadeAnimation: false,
      zoomAnimation: false,
    });
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
    }).addTo(map);

    layersRef.current = L.layerGroup().addTo(map);

    return () => {
      destroyMap(mapRef.current);
      mapRef.current = null;
      layersRef.current = null;
      if (el._leaflet_id != null) {
        el.replaceChildren();
        delete el._leaflet_id;
      }
    };
  }, [mapKey]);

  // Aggiorna layer senza distruggere la mappa
  useEffect(() => {
    const map = mapRef.current;
    const group = layersRef.current;
    if (!map || !group) return;

    group.clearLayers();

    if (points.length > 1) {
      L.polyline(points, { color: "#2563eb", weight: 4, opacity: 0.85 }).addTo(group);
    }

    if (sede?.lat && sede?.lng) {
      const addr = sede.indirizzo ? `<br/>${sede.indirizzo}` : "";
      L.circleMarker([sede.lat, sede.lng], {
        radius: 10,
        color: "#15803d",
        fillColor: "#22c55e",
        fillOpacity: 0.9,
        weight: 2,
      })
        .bindPopup(`<strong>Sede (partenza/arrivo)</strong>${addr}`)
        .addTo(group);
    }

    fermate.forEach((f, i) => {
      if (!f.lat || !f.lng) return;
      const label = `${i + 1}. ${f.ospite_nome || f.struttura_nome || "Fermata"}`;
      const addr = f.indirizzo ? `<br/>${f.indirizzo}` : "";
      L.marker([f.lat, f.lng]).bindPopup(`<strong>${label}</strong>${addr}`).addTo(group);
    });

    const fitOpts = { padding: [24, 24], animate: false };
    if (points.length > 1) {
      try {
        const bounds = L.latLngBounds(points);
        if (sede?.lat && sede?.lng) {
          bounds.extend([sede.lat, sede.lng]);
        }
        map.fitBounds(bounds, fitOpts);
      } catch {
        map.setView(center, 12, { animate: false });
      }
    } else if (sede?.lat && sede?.lng) {
      map.setView([sede.lat, sede.lng], 12, { animate: false });
    } else if (center) {
      map.setView(center, 12, { animate: false });
    }
  }, [mapKey, center, points, fermate, sede]);

  return (
    <div
      ref={containerRef}
      style={{ height: "100%", width: "100%" }}
      aria-label="Mappa percorso pulmini"
    />
  );
}

export default function CdRouteMap({ fermate = [], polyline = "", sede = null, height = "280px", className = "" }) {
  const points = useMemo(() => {
    if (polyline) return decodePolyline(polyline);
    return fermate.filter((f) => f.lat && f.lng).map((f) => [f.lat, f.lng]);
  }, [fermate, polyline]);

  const center = useMemo(() => {
    if (sede?.lat && sede?.lng) return [sede.lat, sede.lng];
    if (points.length) return points[0];
    const f = fermate.find((x) => x.lat && x.lng);
    if (f) return [f.lat, f.lng];
    return [41.4675, 12.9036];
  }, [points, fermate, sede]);

  const mapKey = useMemo(() => {
    const ids = fermate.map((f) => f.fascicolo_id || `${f.lat},${f.lng}`).join("|");
    const sedeKey = sede?.lat ? `${sede.lat},${sede.lng}` : "";
    return `${ids}:${polyline?.length ?? 0}:${sedeKey}`;
  }, [fermate, polyline, sede]);

  if (!fermate.length && !polyline) {
    return (
      <div
        className={`rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 text-sm ${className}`}
        style={{ height }}
      >
        Nessun percorso disponibile
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl overflow-hidden border border-slate-200 ${className}`}
      style={{ height }}
    >
      <LeafletMapCanvas
        mapKey={mapKey}
        center={center}
        points={points}
        fermate={fermate}
        sede={sede}
      />
    </div>
  );
}
